import React, {
  KeyboardEvent,
  SyntheticEvent,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';

import { Input, Text, TextLink } from '@desygner/ui-common-components';

import { StyledDashedDivider } from '../AssetColorPaletteControls/AssetColorPaletteControls.style';

export type EditableTextProps = {
  value: string | undefined;
  onEnter: (value?: string, event?: React.MouseEvent | React.TouchEvent) => void;
  onBlur?: (value?: string, event?: SyntheticEvent) => void;
  id: string;
  label: string;
  link?: string;
  isValueShownOnEdit?: boolean;
  isBottomDashedLineShown?: boolean;
  areMultipleEditsAllowed?: boolean;
  isEditableOnDoubleClick?: boolean;
};

export interface EditableTextRef {
  openEdit: (opened: boolean) => void;
}

export const EditableText = forwardRef<EditableTextRef, EditableTextProps>(function EditableText(
  {
    value,
    onEnter,
    onBlur,
    id,
    label,
    link,
    areMultipleEditsAllowed = false,
    isEditableOnDoubleClick = false,
    isValueShownOnEdit = false,
    isBottomDashedLineShown = false,
  },
  ref
) {
  const [isEditable, setIsEditable] = useState(false);
  const [resultValue, setResultValue] = useState(value);

  useImperativeHandle(ref, () => ({
    openEdit: (opened: boolean) => setIsEditable(opened),
  }));

  const onHandleInput = () => {
    setIsEditable(false);
  };

  const onHandleEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onEnter(resultValue);
      onHandleInput();
    }
  };

  const onHandleBlur = (e: SyntheticEvent) => {
    onBlur?.(resultValue, e);
    onHandleInput();
  };

  const getLink = () => {
    let resultLink = link ?? '';

    if (resultValue) {
      resultLink += resultValue;
    }

    return resultLink;
  };

  const renderEditableTitle = () => (
    <div
      onClick={(event: SyntheticEvent) => {
        event?.stopPropagation();
        setIsEditable(true);
      }}
    >
      <Text children={label} size="Body" />
      {isBottomDashedLineShown && <StyledDashedDivider />}
    </div>
  );

  const renderEditableTitleOnDoubleClick = () => (
    <div
      onClick={(event) => {
        event?.stopPropagation();
      }}
      onDoubleClick={() => setIsEditable(true)}
    >
      <Text children={label} size="Body" />
      {isBottomDashedLineShown && <StyledDashedDivider />}
    </div>
  );
  /**@TODO Send a new prop to identify if the incoming value is a text or a link.  */

  const renderText = () =>
    resultValue ? (
      <TextLink children={resultValue} size="Body" href={getLink()} target="_blank" />
    ) : (
      <div onClick={() => setIsEditable(true)}>
        <Text children={label} size="Body" />
      </div>
    );

  return isEditable ? (
    <Input
      id={id}
      name={id}
      onBlur={(e) => onHandleBlur(e)}
      onKeyDown={onHandleEnter}
      value={isValueShownOnEdit ? value : ''}
      autofocus
      onChange={(value) => setResultValue(value)}
    />
  ) : !areMultipleEditsAllowed ? (
    renderText()
  ) : isEditableOnDoubleClick ? (
    renderEditableTitleOnDoubleClick()
  ) : (
    renderEditableTitle()
  );
});
